import { useState } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import FolderIcon from "@mui/icons-material/Folder";
import type { VectorStoreResponse as VectorStore } from "../../api/generated";

const SCOPE_LABELS: Record<string, string> = {
  org: "Org",
  team: "Team",
  individual: "Personal",
  system: "System",
};

const SCOPE_COLORS: Record<
  string,
  "default" | "primary" | "secondary" | "info"
> = {
  org: "primary",
  team: "secondary",
  individual: "info",
  system: "default",
};

interface VectorStoreListProps {
  stores: VectorStore[];
  onSelect: (store: VectorStore) => void;
  onEdit: (store: VectorStore) => void;
  onDelete: (store: VectorStore) => void;
}

export function VectorStoreList({
  stores,
  onSelect,
  onEdit,
  onDelete,
}: VectorStoreListProps) {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuStore, setMenuStore] = useState<VectorStore | null>(null);

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    store: VectorStore,
  ) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuStore(store);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuStore(null);
  };

  if (stores.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 6 }}>
        <FolderIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
        <Typography sx={{
          color: "text.secondary"
        }}>No knowledge bases yet.</Typography>
        <Typography variant="body2" sx={{
          color: "text.disabled"
        }}>
          Create one to start uploading documents.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {stores.map((store) => (
        <Card key={store.id} variant="outlined">
          <CardActionArea
            onClick={() => onSelect(store)}
            sx={{ display: "flex", p: 0 }}
          >
            <CardContent sx={{ flex: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="h6">
                  {store.name}
                </Typography>
                <Chip
                  label={SCOPE_LABELS[store.scope ?? "org"] ?? store.scope}
                  size="small"
                  color={SCOPE_COLORS[store.scope ?? "org"] ?? "default"}
                />
                <Chip
                  label={`${store.file_count} files`}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={`${store.total_chunks} chunks`}
                  size="small"
                  variant="outlined"
                />
              </Box>
              {store.description && (
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    mt: 0.5
                  }}>
                  {store.description}
                </Typography>
              )}
            </CardContent>
            <Box sx={{ pr: 1, display: "flex", alignItems: "center" }}>
              <IconButton
                size="small"
                onClick={(e) => handleMenuOpen(e, store)}
              >
                <MoreVertIcon />
              </IconButton>
            </Box>
          </CardActionArea>
        </Card>
      ))}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            if (menuStore) onEdit(menuStore);
            handleMenuClose();
          }}
        >
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuStore) onDelete(menuStore);
            handleMenuClose();
          }}
          sx={{ color: "error.main" }}
        >
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
}
